import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ActionService } from 'src/app/services/actions/action.service';
import { AlertService, MessageSeverity } from 'src/app/services/alert.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { DeleteDocumentTypeRequest, DeleteDocumentTypeResponse, GetDocumentTypeRequest, GetDocumentTypeResponse, SaveDocumentTypeRequest, SaveDocumentTypeResponse } from 'src/app/services/document-type/document-type-plugin-models';
import { DocumentType, DocumentTypeFunctionalAreasSelected, MetedataELynk } from 'src/app/services/document-type/document-type.model';
import { DocumentTypeBaseComponent } from '../document-types-base.component';
@Component({
    selector: 'app-document-type-common',
    templateUrl: './document-type-common.component.html',
})
export class DocumentTypeCommonComponent extends DocumentTypeBaseComponent implements OnChanges {

    constructor(
        private alertService: AlertService,
        private confirmationService: ConfirmationService,
        public actionService: ActionService,
        public configurationService: ConfigurationService


    ) {
        super(configurationService, actionService);
    }

    documentType: DocumentType = new DocumentType();
    initialDocumentType: DocumentType;
    functionalAreasConfig: DocumentTypeFunctionalAreasSelected = new DocumentTypeFunctionalAreasSelected();

    @Input() documentTypeId: number;

    @Output() documentTypeIdChange = new EventEmitter<number>();
    @Output() documentTypeRemoved = new EventEmitter<number>();
    @Output() documentTypeAdded = new EventEmitter<DocumentType>();
    @Output() areaConfigChange = new EventEmitter<DocumentTypeFunctionalAreasSelected>();

    @ViewChild('docTypeForm', { static: false }) documentTypeForm: NgForm;

    documentTypeChangeWarning: string = 'Caution: Changing the document type may have far reaching effects. Please consider the potential consequences before proceeding.';
    saving: boolean = false;
    displayDocumentTypeWarning: boolean = false;

    get isNewDocumentType(): boolean {
        return this.documentTypeId === 0;
    }

    async ngOnChanges(changes: SimpleChanges) {
        this.displayDocumentTypeWarning = false;

        if (changes.documentTypeId?.currentValue > 0) {
            await this.setActiveDocumentType(+changes.documentTypeId?.currentValue);
            this.initialDocumentType = JSON.parse(JSON.stringify(this.documentType));
            this.setFunctionalAreasConfig();
        } else {
            this.documentType = new DocumentType();
        }

    }

    async setActiveDocumentType(documentTypeId: number) {
        this.documentType = await this.getDocumentType(documentTypeId);
    }

    clearForm() {
        this.documentTypeId = 0;
        this.documentTypeForm.reset();
    }

    async saveChanges() {

        if (this.saving) return; //prevent multiple clicks

        this.saving = true;
        try {
            if (this.isNewDocumentType || (this.initialDocumentType.type === this.documentType.type)) {
                await this.doSave();
            }
            else {
                const docType = this.documentType.type;
                const intialDocType = this.initialDocumentType.type
                const msg = `<strong>Caution:</strong> Changing the document type's "type" value may have far reaching effects. <br />Please consider the potential consequences before proceeding.<br /> Are you sure you want to change "${intialDocType}" to  "${docType}" ?`;

                this.confirmationService.confirm({
                    header: 'Confirm Document Type Change',
                    key: 'DocTypeDialog',
                    icon: 'pi pi-exclamation-triangle tw-text-red-500',
                    message: msg,
                    accept: async () => {
                        await this.doSave();
                    },
                    reject: () => {
                        return;
                    }
                });
            }
        } finally {
            this.saving = false;
        }
    }

    async doSave() {

        try {

            if (this.isNewDocumentType) {
                await this.checkForPotentialConflict();
            }

            const idStart = +this.documentType.id;

            const saveResponse = await this.saveDocumentType(this.documentType);
            if (saveResponse.statusCode === 200) {
                this.documentTypeId = saveResponse.documentTypeId;
                this.documentType.id = this.documentTypeId;
                this.documentTypeIdChange.emit(this.documentTypeId);
                this.documentTypeForm.form.markAsPristine();

                this.alertService.showMessage(`Document type "${this.documentType.type}" saved.`, null, MessageSeverity.success);
                this.initialDocumentType = JSON.parse(JSON.stringify(this.documentType));

                if (idStart === 0) {
                    this.documentTypeAdded.emit(this.documentType);
                }
            } else {
                this.alertService.showMessage(`Failed to save document type "${this.documentType.type}".`, "Unknown error ocurred while saving document type.", MessageSeverity.error);
            }

        } catch (error) {
            this.alertService.showMessage(`Failed to save document type "${this.documentType.type}".`, error.message, MessageSeverity.error);
        }
    }

    async checkForPotentialConflict() {
        let documentTypes = await this.getDocumentTypes();
        const index = documentTypes.findIndex(documentType => documentType.type === this.documentType.type && documentType.version === this.documentType.version);

        if (index > -1) {
            throw new Error('Document type with the same type and version already exists.');
        }

    }
    resetForm() {
        this.documentTypeForm.resetForm(
            {
                type: this.initialDocumentType.type,
                version: this.initialDocumentType.version,
                metadata: this.initialDocumentType.metadata,
                description: this.initialDocumentType.description,
                active: this.initialDocumentType.active,
                showInUI: this.initialDocumentType.showInUI
            }
        );
    }

    async delete(event) {

        const docType = `${this.documentType.description ?? this.documentType.type} v. ${this.documentType.version}`;
        const msg = `<strong>Caution:</strong> Deleting document type may have far reaching effects. <br />Please consider the potential consequences before proceeding.<br /> Are you sure you want to delete ${docType}?`;

        this.confirmationService.confirm({
            target: event.target,
            key: 'DocTypeDialog',
            icon: 'pi pi-exclamation-triangle tw-text-red-500',
            message: msg,
            accept: async () => {
                try {
                    const deleteResponse = await this.deleteDocumentType(this.documentTypeId);

                    if (deleteResponse.statusCode === 200) {
                        this.clearForm();

                        this.documentType = new DocumentType();
                        this.documentTypeId = 0;
                        this.functionalAreasConfig = new DocumentTypeFunctionalAreasSelected();

                        this.documentTypeRemoved.emit(this.documentTypeId);
                        this.documentTypeIdChange.emit(this.documentTypeId);

                        this.alertService.showMessage(`Document type "${docType}" deleted.`, null, MessageSeverity.success);
                    } else {
                        this.alertService.showMessage(`Failed to delete document type "${docType}".`, "Unknown error ocurred during the deletion of document type.", MessageSeverity.error);
                    }

                } catch (error) {
                    this.alertService.showMessage(`Failed to delete document type "${docType}".`, error.error.Message, MessageSeverity.error);
                }
            },
            reject: () => {
                return;
            }
        });
    }

    setFunctionalAreasConfig() {
        this.functionalAreasConfig = (this.documentTypeId === 0) ? new DocumentTypeFunctionalAreasSelected() : this.getDocumentTypeFunctionalAreas();
    }

    getDocumentTypeFunctionalAreas(): DocumentTypeFunctionalAreasSelected {

        return {
            elynk: this.documentType?.metadata?.elynk?.packets.length > 0,
            templateManager: this.documentType?.metadata?.templateManager != null,
            documentClassification: this.documentType?.metadata?.classification != null,
        } as DocumentTypeFunctionalAreasSelected;
    }

    documentTypeChanged(event) {
        this.displayDocumentTypeWarning = (!this.isNewDocumentType) && (this.documentType?.type !== this.initialDocumentType?.type);
    }

    async deleteElynk(event) {

        if (!event.checked) {

            const docType = `${this.documentType.description ?? this.documentType.type} v. ${this.documentType.version}`;
            const msg = `Deselecting eLynk will delete the eLynk configuration for ${docType}?.  Continue?`;

            this.confirmationService.confirm({
                target: event.target,
                key: 'DocTypeDialog',
                message: msg,
                accept: async () => {
                    try {
                        this.documentType.metadata.elynk = new MetedataELynk();
                        const saveResponse = await this.saveDocumentType(this.documentType);

                        if (saveResponse.statusCode === 200) {
                            this.alertService.showMessage(`Document type eLynk configuration for "${docType}" deleted.`, null, MessageSeverity.success);
                            this.areaConfigChange.emit({ elynk: false, templateManager: true, documentClassification: true } as DocumentTypeFunctionalAreasSelected);
                        } else {
                            this.alertService.showMessage(`Failed to delete eLynk configuration for document type "${docType}".`, "Unknown error ocurred during the deletion of document type.", MessageSeverity.error);
                        }

                    } catch (error) {
                        this.alertService.showMessage(`Failed to delete eLynk configuration for document type "${docType}".`, error.error.Message, MessageSeverity.error);
                    }
                },
                reject: () => {
                    return;
                }
            });

        }

    }

    async deleteTemplateManager(event) {

        if (!event.checked) {

            const docType = `${this.documentType.description ?? this.documentType.type} v. ${this.documentType.version}`;
            const msg = `Deselecting Template Manager will delete the template manager configuration for ${docType}.  Continue?`;

            this.confirmationService.confirm({
                target: event.target,
                key: 'DocTypeDialog',
                message: msg,
                accept: async () => {
                    try {
                        this.documentType.metadata.templateManager = null;
                        const saveResponse = await this.saveDocumentType(this.documentType);

                        if (saveResponse.statusCode === 200) {
                            this.alertService.showMessage(`Document type "${docType}" template manager configuration deleted.`, null, MessageSeverity.success);
                            this.areaConfigChange.emit({ elynk: true, templateManager: false, documentClassification: true } as DocumentTypeFunctionalAreasSelected);
                        } else {
                            this.alertService.showMessage(`Failed to delete template manager configuration for document type "${docType}".`, "Unknown error ocurred during the deletion of document type.", MessageSeverity.error);
                        }

                    } catch (error) {
                        this.alertService.showMessage(`Failed to delete template manager configuration for document type "${docType}".`, error.error.Message, MessageSeverity.error);
                    }
                },
                reject: () => {
                    return;
                }
            });
        }

    }

    async deleteClassification(event) {

        if (!event.checked) {

            const docType = `${this.documentType.description ?? this.documentType.type} v. ${this.documentType.version}`;
            const msg = `Deselecting Document Classification will delete the classification configuration for ${docType}.  Continue?`;

            this.confirmationService.confirm({
                target: event.target,
                key: 'DocTypeDialog',
                message: msg,
                accept: async () => {
                    try {
                        this.documentType.metadata.classification = null;
                        const saveResponse = await this.saveDocumentType(this.documentType);

                        if (saveResponse.statusCode === 200) {
                            this.alertService.showMessage(`Document type classification "${docType}" deleted.`, null, MessageSeverity.success);
                            this.areaConfigChange.emit({ elynk: true, templateManager: true, documentClassification: false } as DocumentTypeFunctionalAreasSelected);
                        } else {
                            this.alertService.showMessage(`Failed to delete classification for document type "${docType}".`, "Unknown error ocurred during the deletion of document type.", MessageSeverity.error);
                        }

                    } catch (error) {
                        this.alertService.showMessage(`Failed to delete classification for document type "${docType}".`, error.error.Message, MessageSeverity.error);
                    }
                },
                reject: () => {
                    return;
                }
            });
        }

    }

    private async getDocumentType(documentTypeId: number): Promise<DocumentType> {
        const request = new GetDocumentTypeRequest(documentTypeId);
        let response = await this.actionService.invoke(this.ActionType, "document-type-get", request) as GetDocumentTypeResponse;
        return response.documentType;
    }

    private async saveDocumentType(documentType: DocumentType): Promise<SaveDocumentTypeResponse> {
        const request = new SaveDocumentTypeRequest(documentType);
        let response = await this.actionService.invoke(this.ActionType, "document-type-save", request) as SaveDocumentTypeResponse;
        return response;
    }

    private async deleteDocumentType(documentTypeId: number): Promise<DeleteDocumentTypeResponse> {
        const request = new DeleteDocumentTypeRequest(documentTypeId);
        let response = await this.actionService.invoke(this.ActionType, "document-type-delete", request) as DeleteDocumentTypeResponse;
        return response;
    }
}
